import React from "react";
import { useNavigate } from "react-router-dom";

import { Table } from "../../../components/shared";
import useSearchAndFilter from "../../../hooks/useSearchAndFilter";
import { Loader } from "../../../components/loaders/loaders";
import {
  ApiResponse,
  xcelaratorTableConfig,
} from "../XcelaratorApi/xcelaratorTypes";

import { AddIcon, FilterIcon, FilterIconWhite } from "../../../assets";
import { agentApiColumns } from "./AgentApi-types";
import { Badge } from "@mui/material";
import FilterSideMenu from "../../../components/shared/FilterSideMenu";
import { DEFAULT_PAGE_SIZE } from "../../../utils/constants";

/**
 * The `XcelaratorApi` component is responsible for displaying and managing
 * a table of Xcelarator API data. It includes search and filter functionality,
 * as well as options to create new entries, edit, delete, and view profiles.
 */
const AgentApi: React.FC = () => {
  const navigate = useNavigate();

  /**
   * Maps raw API response data to a format suitable for the table component.
   *
   * @param {ApiResponse[]} userData - Array of user data from the API.
   * @returns - Mapped array of user data.
   */
  const mapUserData = (userData: ApiResponse[]) =>
    userData.map((user) => ({
      id: user.id,
      count: user.count,
      agentName: user?.agent?.name || "",
      agentId: user?.agent?.id || "",
      clientCode: user?.clientCode || "",
      hubName: user?.agentHub?.name || "",
      clientApiKey: user?.clientAPI || "--",
      apiType: user?.apiType || "",
      active: user?.active ?? false,
      logo: user?.logo ?? null,
    }));

  // Custom hook to handle search, filter, and fetch data functionality
  const {
    data,
    loading,
    setLoading,
    fetchFilteredData,
    fetchWithCurrentFilters,
    closeOpenfilterModal,
    isShowFilterModal,
    selectStatus,
    setSelectStatus,
    filterApplied,
    setFilterApplied,
    agentName,
    setAgentName,
    captureAppliedFilters,
    clearCurrentFilters,
  } = useSearchAndFilter<ApiResponse>({
    apiUrl: "/agent/clientCredential/agentcredlist",
    limit: DEFAULT_PAGE_SIZE,
    mapUserData,
    extentedUrl: "",
    screenKey: "agentApi",
  });

  /**
   * Handles navigation to a specified URL.
   *
   * @param {string} url - The URL to navigate to.
   */
  const handleNavigateUser = (url: string) => {
    navigate(url);
  };

  if (loading) {
    return <Loader />;
  }

  return (
    <div
        className={`tableWrap ${
          isShowFilterModal ? "tableWrap--filterOpen" : ""
        }`}>
      <div className="tableWrap__head tableWrap__head--end">
        <div className="btn__group">
        {filterApplied ? (
                <Badge badgeContent=" " color="primary">
                  <button
                    className={
                      !isShowFilterModal ? "btn btn__white" : "btn btn__primary"
                    }
                    onClick={closeOpenfilterModal}
                  >
                    {!isShowFilterModal ? <FilterIcon /> : <FilterIconWhite />}
                    Filter
                  </button>
                </Badge>
              ) : (
                <button
                  className={
                    !isShowFilterModal ? "btn btn__white" : "btn btn__primary"
                  }
                  onClick={() => {
                    // setShowEditColumn(false);
                    closeOpenfilterModal();
                  }}
                >
                  {!isShowFilterModal ? <FilterIcon /> : <FilterIconWhite />}
                  Filter
                </button>
            )}
            <FilterSideMenu
              isShowFilterModal={isShowFilterModal}
              closeOpenfilterModal={closeOpenfilterModal}
              selectStatus={selectStatus}
              setSelectStatus={setSelectStatus}
              isStatusHide={false}
              isRoleHide={true}
              isOrderStatusHide={true}
              isOrderTypeHide={true}
              isAgentTypeHide={true}
              isHubHide={true}
              isServiceHide={true}
              isCustomerTypeHide={false}
              isAllCustomerTypeHide={true}
              showAgentName = {true}
              agentName = {agentName}
              setAgentName={setAgentName}
              fetchFilteredData={fetchFilteredData}
              onFiltersApplied={captureAppliedFilters}
              filterApplied = {filterApplied}
              setFilterApplied = {setFilterApplied}
              table= "agentApi"
              clearFiltersFromStorage={clearCurrentFilters}
            />
          <button
            className="btn btn__white"
            onClick={() => {
              handleNavigateUser("/agent-api/add");
            }}
          >
            <AddIcon />
            Create New
          </button>
        </div>
      </div>
      <Table
        columns={agentApiColumns}
        data={data}
        setLoading={setLoading}
        onEditNaviagte={xcelaratorTableConfig.edit}
        onDeleteConfig={xcelaratorTableConfig.delete}
        profileLinkNaviagte={xcelaratorTableConfig.profileLink}
        getApiData={fetchWithCurrentFilters}
        deleteIcon={true}
        editIcon={true}
        activeIcon={true}
      />
    </div>
  );
};

export default AgentApi;
